import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getBillingRefusal, type BillingRefusal } from "../../../api";
import { useBoard } from "../../../hooks";
import { BoardConnection, Source } from "../../../types";
import type {
  ConnectionsLevelViewModel as ConnectionsLevelViewModelContract,
  ConnectionsLevelViewModelProps as ConnectionsLevelViewModelPropsContract,
} from "./ConnectionsLevel.ViewModel.types";
import {
  parseStoredVotes,
  SOURCE_VOTES_KEY,
  sortConnections,
  sortSources,
} from "./Connections.helpers";

export const useConnectionsLevelViewModel = ({
  boardId,
  hubConnectionId,
  boardsApi,
  sourcesApi,
  loadErrorMessage,
  saveErrorMessage,
  deleteErrorMessage,
  voteStorage,
  feedbackApi,
  showBacklog,
  additionalPlannedSources,
}: ConnectionsLevelViewModelPropsContract): ConnectionsLevelViewModelContract => {
  const boardQuery = useBoard(boardsApi, boardId || "", { enabled: !!boardId });
  const [sources, setSources] = useState<Source[]>([]);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [isCreatingConnection, setIsCreatingConnection] = useState(false);
  const [isSavingConnection, setIsSavingConnection] = useState(false);
  const [isDeletingConnection, setIsDeletingConnection] = useState(false);
  const [isAuthorisingConnection, setIsAuthorisingConnection] = useState(false);
  const [sourcesFailed, setSourcesFailed] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [votedSourceIds, setVotedSourceIds] = useState<string[]>([]);
  const [actionErrorMessage, setActionErrorMessage] = useState("");
  const pendingVoteIds = useRef(new Set<string>());
  const [billingRefusal, setBillingRefusal] = useState<BillingRefusal | null>(null);

  useEffect(() => {
    if (!voteStorage) {
      return;
    }

    setVotedSourceIds(parseStoredVotes(voteStorage.getItem(SOURCE_VOTES_KEY)));
  }, [voteStorage]);

  const loadSources = useCallback(async (): Promise<void> => {
    setIsLoadingSources(true);
    setSourcesFailed(false);

    try {
      const nextSources = await sourcesApi.getSources();
      setSources(nextSources.sort(sortSources));
    } catch (error) {
      setSourcesFailed(true);
      console.error("Failed to load sources", error);
    } finally {
      setIsLoadingSources(false);
    }
  }, [sourcesApi]);

  useEffect(() => {
    if (!boardId) {
      setSources([]);
      setIsLoadingSources(false);
      setSourcesFailed(false);
      return;
    }

    void loadSources();
  }, [boardId, loadSources]);

  const reload = useCallback(async (): Promise<void> => {
    await Promise.all([
      loadSources(),
      boardQuery.refetch ? boardQuery.refetch() : Promise.resolve(),
    ]);
  }, [boardQuery, loadSources]);

  const getSourceById = useCallback(
    (sourceId: string): Source | undefined =>
      sources.find(
        (source) => source.id.toLowerCase() === sourceId.toLowerCase(),
      ),
    [sources],
  );

  const createConnection = useCallback(
    async (sourceId: string): Promise<BoardConnection | null> => {
      if (!boardId) {
        return null;
      }

      setIsCreatingConnection(true);
      setActionErrorMessage("");
      setBillingRefusal(null);

      try {
        const created = await boardsApi.createConnection(
          boardId,
          sourceId,
          hubConnectionId || undefined,
        );
        if (boardQuery.refetch) {
          await boardQuery.refetch();
        }
        return created;
      } catch (error) {
        // A plan refusal isn't a failure: it's shown as an upgrade prompt (or a "try again") instead.
        const refusal = getBillingRefusal(error);
        if (refusal) {
          setBillingRefusal(refusal);
          return null;
        }

        console.error("Failed to create connection", error);
        setActionErrorMessage(saveErrorMessage || loadErrorMessage);
        return null;
      } finally {
        setIsCreatingConnection(false);
      }
    },
    [
      boardId,
      boardQuery,
      boardsApi,
      hubConnectionId,
      saveErrorMessage,
      loadErrorMessage,
    ],
  );

  const saveConnection = useCallback(
    async (connection: BoardConnection): Promise<boolean> => {
      if (!boardId) {
        return false;
      }

      setIsSavingConnection(true);
      setActionErrorMessage("");
      setBillingRefusal(null);

      try {
        await boardsApi.updateConnection(
          boardId,
          connection.id,
          connection,
          hubConnectionId || undefined,
        );
        if (boardQuery.refetch) {
          await boardQuery.refetch();
        }
        return true;
      } catch (error) {
        // Un-archiving counts against the plan like a new connection.
        const refusal = getBillingRefusal(error);
        if (refusal) {
          setBillingRefusal(refusal);
          return false;
        }

        console.error("Failed to update connection", error);
        setActionErrorMessage(saveErrorMessage || loadErrorMessage);
        return false;
      } finally {
        setIsSavingConnection(false);
      }
    },
    [
      boardId,
      boardQuery,
      boardsApi,
      hubConnectionId,
      saveErrorMessage,
      loadErrorMessage,
    ],
  );

  const deleteConnection = useCallback(
    async (connectionId: string): Promise<boolean> => {
      if (!boardId) {
        return false;
      }

      setIsDeletingConnection(true);
      setActionErrorMessage("");

      try {
        await boardsApi.deleteConnection(
          boardId,
          connectionId,
          hubConnectionId || undefined,
        );
        if (boardQuery.refetch) {
          await boardQuery.refetch();
        }
        return true;
      } catch (error) {
        console.error("Failed to delete connection", error);
        setActionErrorMessage(deleteErrorMessage || loadErrorMessage);
        return false;
      } finally {
        setIsDeletingConnection(false);
      }
    },
    [
      boardId,
      boardQuery,
      boardsApi,
      hubConnectionId,
      deleteErrorMessage,
      loadErrorMessage,
    ],
  );

  const connections = useMemo(
    () => [...(boardQuery.data?.connections || [])].sort(sortConnections),
    [boardQuery.data?.connections],
  );

  const allSources = useMemo(() => {
    if (!additionalPlannedSources || additionalPlannedSources.length === 0) {
      return sources;
    }
    const apiIds = new Set(sources.map((s) => s.id.toLowerCase()));
    const newPlanned = additionalPlannedSources.filter(
      (s) => !apiIds.has(s.id.toLowerCase()),
    );
    if (newPlanned.length === 0) {
      return sources;
    }
    return [...sources, ...newPlanned].sort(sortSources);
  }, [sources, additionalPlannedSources]);

  const filteredSources = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return allSources.filter((source) => {
      if (!showBacklog && source.backlog) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return (
        source.displayName.toLowerCase().includes(normalizedSearch) ||
        source.description.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [searchText, showBacklog, allSources]);

  const connectedPrivacySources = useMemo(() => {
    const connectionSourceIds = new Set(
      connections.map((connection) => connection.sourceTypeId.toLowerCase()),
    );

    return sources.filter(
      (source) =>
        !!source.securityDescription &&
        connectionSourceIds.has(source.id.toLowerCase()),
    );
  }, [connections, sources]);

  const voteForSource = useCallback(
    (sourceId: string): void => {
      // allSources, not getSourceById: planned sources are not in the API list.
      const source = allSources.find(
        (candidate) => candidate.id.toLowerCase() === sourceId.toLowerCase(),
      );
      if (
        !source ||
        votedSourceIds.includes(sourceId) ||
        pendingVoteIds.current.has(sourceId)
      ) {
        return;
      }

      // Mark it voted straight away so the button locks; undo if the post fails
      // so the user can try again. Only a vote the region accepted is remembered.
      pendingVoteIds.current.add(sourceId);
      setVotedSourceIds((previous) => [...previous, sourceId]);

      void feedbackApi
        .featureVote({ feature: source.displayName, boardId: boardId ?? null })
        .then(() => {
          const stored = parseStoredVotes(
            voteStorage?.getItem(SOURCE_VOTES_KEY) ?? null,
          );
          if (!stored.includes(sourceId)) {
            voteStorage?.setItem(
              SOURCE_VOTES_KEY,
              JSON.stringify([...stored, sourceId]),
            );
          }
        })
        .catch(() => {
          setVotedSourceIds((previous) =>
            previous.filter((id) => id !== sourceId),
          );
        })
        .finally(() => {
          pendingVoteIds.current.delete(sourceId);
        });
    },
    [allSources, boardId, feedbackApi, voteStorage, votedSourceIds],
  );

  const hasVotedForSource = useCallback(
    (sourceId: string): boolean => votedSourceIds.includes(sourceId),
    [votedSourceIds],
  );

  return {
    isLoadingSources,
    isCreatingConnection,
    isSavingConnection,
    isDeletingConnection,
    isAuthorisingConnection,
    setIsAuthorisingConnection,
    sourcesFailed,
    searchText,
    setSearchText,
    actionErrorMessage,
    setActionErrorMessage,
    billingRefusal,
    setBillingRefusal,
    allSources,
    connections,
    filteredSources,
    connectedPrivacySources,
    votedSourceIds,
    voteForSource,
    hasVotedForSource,
    reload,
    getSourceById,
    createConnection,
    saveConnection,
    deleteConnection,
  };
};
